/******************************************************************************
 *                                                                            *
 *  Copyright (c) 2018, 2025 by AsterionDB Inc.                               *
 *                                                                            *
 *  All rights reserved.  No part of this work may be reproduced or otherwise *
 *  incorporated into other works without the express written consent of      *
 *  AsterionDB Inc.                                                           *
 *                                                                            *
 *****************************************************************************/

import React from 'react'
import { Row, Col, FormGroup, FormFeedback, Label, Input, Button } from 'reactstrap'

import PasswordWidget from '../widgets/PasswordWidget.js'
import { AppContext } from '../AppContext'
import TermsOfService from '../widgets/TermsOfService.js';

class Signup extends React.Component
{

  usernameIsAvailable = true;
  emailAddressIsNotUsedBefore = true;

  emailAddressElement = null;
  middleNameElement = null;

  invitationToken = null;

  async checkAvailableUsernames(event)
  {
    var bodyData = {};
    bodyData.username = this.state.username;
    let result = await this.context.dbTwig.callRestAPI('icam', 'validateNewUsername', bodyData);
    switch (result.status)
    {
      case 'success':
        break;

      case 'apiError':
        if (this.context.dbTwig.UsernameExists === result.errorCode)
        {
          this.setState(
          {
            usernameValid: false,
            usernameFB: 'That username is not available.  Please choose another.'
          });
          break;
        }

        this.context.dbTwig.apiErrorHandler(result, 'Unable to validate new username.');
        break;

      default:
        this.context.dbTwig.apiErrorHandler(result, 'Unable to validate new username.');
        break;
    }
  }

  async checkEmailAddress(emailAddress)
  {
    var bodyData = {};
    bodyData.emailAddress = emailAddress;
    let result = await this.context.dbTwig.callRestAPI('icam', 'validateNewEmailAddress', bodyData);
    switch (result.status)
    {
      case 'success':
        break;

      case 'apiError':
        if (this.context.dbTwig.EmailExists === result.errorCode)
        {
          this.setState(
          {
            emailAddressValid: false,
            emailAddressFB: 'This email has already been registered in the system.  Try recovering your account.'
          });
          break;
        }

        this.context.dbTwig.apiErrorHandler(result, 'Unable to validate new email address.');
        break;

      default:
        this.context.dbTwig.apiErrorHandler(result, 'Unable to validate new email address.');
        break;
    }
  }

  // Old code kept in case invites come back.
  
  async checkInvitation()
  {
    // because we only expect a single argument,
    // instead of parsing multiple query arguments, 
    // we can actually treat this as a security
    // enhancement and ignore requests that aren't
    // what we expect
    if (window.location.search.substring(0, 1) !== '?'
     || /[^0-9a-zA-Z]/.test(window.location.search.substring(1)))
    {
      // redirect back to login page
      // to external URI:
      // window.location.href = this.redirectURI;
      // can use history.push() if you want the current page to stay in the browser history
      this.props.history.replace(window.redirectURI);
      return;
    }

    // by taking the entire query string without checking, if the user
    // adds anything funny, they will just invalidate their request
    // (as long as the DB passes this entire string to its decrypt function)
    this.invitationToken = window.location.search.substring(1);
    let bodyData = {invitationToken: this.invitationToken};
    
    // check if what appears to be a valid token *really* is valid
    let result = await this.context.dbTwig.callRestAPI('icam', 'validateInvitationToken', bodyData);
    if ('apiError' === result.status && this.context.dbTwig.InvalidConfirmationData === result.errorCode) 
      this.props.history.replace(window.redirectURI);
    if ('success' !== result.status) return this.context.dbTwig.apiErrorHandler(result, 'Unable to confirm invitation data');

    this.setState(
    {
      emailAddress: result.jsonData.emailAddress, 
      emailAddressValid: true,
      firstName: result.jsonData.firstName, 
      firstNameValid: true,
      lastName: result.jsonData.lastName,
      lastNameValid: true
    });

    this.middleNameElement.focus();
  }

  componentDidMount()
  {
    window.loadZxcvbn();

    if (this.props.invitation) this.checkInvitation();

    this.emailAddressElement.focus();
  }
  
  constructor(props)
  {
    super();

    this.state =
    {
      emailAddress: '',
      emailAddressValid: undefined,
      emailAddressFB: null,
      firstName: '',
      firstNameValid: undefined,
      firstNameFB: null,
      middleName: '',
      lastName: '',
      lastNameValid: undefined,
      lastNameFB: null,
      username: '',
      usernameValid: undefined,
      usernameFB: null,
      usernameDisabled: props.invitation ? true : false,
      password: '',
      passwordValid: undefined,
      passwordFB: null,
      passwordScore: null,
      passwordFocused: false,
      confirm: '',
      confirmValid: undefined,
      confirmFB: null,
      confirmDisabled: true,
      confirmFocused: false,
      termsAccepted: false,
      accountType: 'user',
      buttonDisabled: true,
      confirmationSent: false
    }

    this.signup = this.signup.bind(this);
    this.setEmailAddress = this.setEmailAddress.bind(this);
    this.toggleAcceptTerms = this.toggleAcceptTerms.bind(this);
    this.requiredField = this.requiredField.bind(this);
  }

  debouncedCheckAvailableUsernames = window.debounce(this.checkAvailableUsernames, 500);
  debouncedCheckEmailAddress = window.debounce(this.checkEmailAddress, 500);

  render()
  {
    if (this.state.confirmationSent)
      return(
        <div>
          <Row>
            <Col sm="6">
              <h2>Initial signup successful.  We have sent you a confirmation email.  You must access that email in order to complete the signup process.</h2>
            </Col>
          </Row>
          <Row>
            <Col sm="6">
              <h2>You can close this window/tab.</h2>
            </Col>
          </Row>
        </div>
      );
    else
      return(
        <div>
          <Row>
            <Col sm={{ offset: 1 }}><h3 >SecureObject Vault Registration</h3></Col>
          </Row>
          <FormGroup row>
            <Label for="emailAddress" sm="2">Email Address</Label>
            <Col sm={3}>
              <Input type="text" id="emailAddress" placeholder="Enter your email address"
                value={this.state.emailAddress} onChange={this.setEmailAddress}
                invalid={(undefined === this.state.emailAddressValid ? undefined : !this.state.emailAddressValid)} 
                valid={(undefined === this.state.emailAddressValid ? undefined : this.state.emailAddressValid)} 
                innerRef={(input) => this.emailAddressElement = input} disabled={this.state.usernameDisabled}
              />
              <FormFeedback valid={false}>{this.state.emailAddressFB}</FormFeedback>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="firstName" sm="2">First Name</Label>
            <Col sm="3">
              <Input type="text" id="firstName" placeholder="First name" value={this.state.firstName} onChange={this.requiredField}
                invalid={(undefined === this.state.firstNameValid ? undefined : !this.state.firstNameValid)} 
                valid={(undefined === this.state.firstNameValid ? undefined : this.state.firstNameValid)} 
              />
              <FormFeedback valid={false}>{this.state.firstNameFB}</FormFeedback>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="middleName" sm="2">Middle Name/Initial</Label>
            <Col sm="3">
              <Input type="text" id="middleName" placeholder="Middle Name/Initial" innerRef={(input) => this.middleNameElement = input} 
                value={this.state.middleName} onChange={(event) => {this.setState({middleName: event.target.value})}}
              />
              <FormFeedback id="middleNameFeedback" style={{display:'none'}} />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="lastName" sm="2">Last Name</Label>
            <Col sm="3">
              <Input type="text" id="lastName" placeholder="Last name" value={this.state.lastName} onChange={this.requiredField}
                invalid={(undefined === this.state.lastNameValid ? undefined : !this.state.lastNameValid)} 
                valid={(undefined === this.state.lastNameValid ? undefined : this.state.lastNameValid)} 
                />
              <FormFeedback valid={false}>{this.state.lastNameFB}</FormFeedback>
            </Col>
          </FormGroup>
          <Row>
            <Col sm='2'>Account Type</Col>
            <Col sm='3'>
              <FormGroup className='form-check-inline'>
                <Label check>
                  <Input type="radio" name="accountType" onChange={() => this.setState({accountType: 'user'})} 
                    checked={('user' === this.state.accountType)}
                  /> 
                  Regular (paid)
                </Label>
              </FormGroup>
              <FormGroup className='form-check-inline'>
                <Label check>
                  <Input type="radio" name="accountType" onChange={() => this.setState({accountType: 'evaluator'})} 
                    checked={('evaluator' === this.state.accountType)}
                  /> 
                  Evaluation (free)
                </Label>
              </FormGroup>
            </Col>
          </Row>
          <FormGroup row>
            <Label for="username" sm="2">Username</Label>
            <Col sm="3">
              <Input title='Sorry..you can not user your email address...' type="text" id="username" placeholder="Choose username" value={this.state.username}
                invalid={(undefined === this.state.usernameValid ? undefined : !this.state.usernameValid)} 
                valid={(undefined === this.state.usernameValid ? undefined : this.state.usernameValid)} 
                onChange={this.requiredField}
              />
              <FormFeedback valid={false}>{this.state.usernameFB}</FormFeedback>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label for="password" sm="2">Password</Label>
            <Col sm="3" >
              <PasswordWidget onChange={this.setPasswordVar.bind(this, 'password')} value={this.state.password}
                passwordValid={this.state.passwordValid} focus={this.state.passwordFocused} zxcvbn={this.state.passwordScore}
                passwordFB={this.state.passwordFB} placeholder='Enter your password' id='password'
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col sm={{ size: 3, offset: 2}} >
              <PasswordWidget disabled={this.state.confirmDisabled} onChange={this.setPasswordVar.bind(this, 'confirm')}
                passwordValid={this.state.confirmValid} focus={this.state.confirmFocused} value={this.state.confirm}
                passwordFB={this.state.confirmFB} placeholder='Enter your password again...'
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col sm="2">Terms of Service</Col>
            <Col sm="4">
              <div style={{overflowY: 'scroll', height: '350px'}}>
                <TermsOfService />
              </div>
            </Col>
          </FormGroup>
          <FormGroup row >
            <Label for='acceptTerms' sm="2">Click to accept...</Label>
            <Col sm="1" style={{display: 'flex', alignItems: 'center'}}>
              <Input type="checkbox" id="acceptTerms"  onChange={this.toggleAcceptTerms} checked={this.state.termsAccepted ? "checked" : ''}
                style={{ marginTop: '0px', marginLeft: '0px'}} />
            </Col>
          </FormGroup>
          <Row style={{paddingTop: "10px"}}>
            <Col sm={{ size: 2, offset: 2 }}>
              <Button onClick={this.signup} disabled={this.state.buttonDisabled} color="primary">Register</Button>
            </Col>
          </Row>
        </div>
      );
  }

  requiredField(event)
  {
    let valid = this.state[event.target.id + 'Valid'];
    let feedback = null;

    if ('' === event.target.value)
    {
      valid = false;
      feedback = 'You must provide a value for this field.';
    }
    else
    {
      valid = true;
    }

    if ('username' === event.target.id && '' !== event.target.value && !window.usernameIsValid(event.target.value)) return;
    
    this.setState(
    {
      [event.target.id]: event.target.value, 
      [event.target.id + 'Valid']: valid,
      [event.target.id + 'FB']: feedback
    }, 
    this.setButtonDisabled);

    if ('username' === event.target.id) this.debouncedCheckAvailableUsernames();
  }
  
  setButtonDisabled()
  {
    let disabled = (this.state.emailAddressValid && this.state.firstNameValid && this.state.lastNameValid && 
      this.state.usernameValid && this.state.passwordValid && this.state.confirmValid && this.state.termsAccepted ? false : true)

    this.setState({buttonDisabled: disabled});
  }

  setEmailAddress(event)
  {
    let valid = false;
    let value = event.target.value.trim();

    let emailAddressFB = null;
    let atSign = value.indexOf('@');
    let dot = value.lastIndexOf('.');
    if ('' === value)
    {
      valid = false;
      emailAddressFB = 'You must provide your email address in order to create an account.';
    }

    if ('' !== value && -1 !==  atSign && -1 !== dot && dot > atSign && 
      dot !== (value.length - 1))
    {
      if (!window.emailRegEx.test(value))
      {
        valid = false;
        emailAddressFB = "That does not look like a valid email address";
      }
      else
      {
        this.debouncedCheckEmailAddress(value)
        valid = true;
      }
    }      

    this.setState(
    {
      emailAddress: value, 
      emailAddressFB: emailAddressFB, 
      emailAddressValid: valid
    }, 
    this.setButtonDisabled);
  }

  setPasswordVar(field, value)
  {
    let passwordValid = this.state.passwordValid;
    let confirmValid = this.state.confirmValid;

    let confirmFB = null;
    let passwordFB = null;

    let password = this.state.password;
    let confirm = this.state.confirm;

    let confirmDisabled = this.state.confirmDisabled;

    let passwordScore = this.state.passwordScore;

    switch (field)
    {
      case 'password':
        password = value;
        passwordScore = window.getPasswordScore(password);
        if ('' === password)
        {
          confirm = '';
          passwordValid = false;
          confirmDisabled = true;
          confirmValid = undefined;
          passwordFB = 'You must enter a valid and strong password to continue.';
          passwordScore = null;
        }
        else if (passwordScore.score < this.context.websiteProfile.passwordStrength)
        {
          passwordFB = 'You must enter a stronger password.  The meter has to go green.';
          passwordValid = false;
        }
        else
        {
          confirmDisabled = false;
          passwordValid = true;
        }

        break;

      case 'confirm':
        confirm = value;
        if ('' === confirm)
        {
          confirmValid = false;
          confirmFB = 'You must confirm your new password by typing it in again.';
        }
        else
        {
          confirmValid = true;
        }
        break;

      default:
        break;
    }

    if ('' !== password && '' !== confirm)
    {
      if (password !== confirm)
      {
        confirmFB = 'Your passwords do not match!';
        confirmValid = false;
      }
      else
      {
        passwordValid = true;
        confirmValid = true;
      }
    }

    this.setState(
    {
      password: password,
      passwordValid: passwordValid,
      passwordFB: passwordFB,
      passwordScore: passwordScore,
      confirm: confirm,
      confirmDisabled: confirmDisabled,
      confirmValid: confirmValid,
      confirmFB: confirmFB
    }, 
    this.setButtonDisabled);
  }

  async signup(event)
  {
    this.setState({buttonDisabled: true});
    event.preventDefault();

    var bodyData = {};
    bodyData.emailAddress = this.state.emailAddress;
    bodyData.firstName = this.state.firstName;
    bodyData.middleName = this.state.middleName;
    bodyData.lastName = this.state.lastName;
    bodyData.username = this.state.username;
    bodyData.password = this.state.password;
    bodyData.defaultTimezone = window.timezone.tz.guess();
    bodyData.invitationToken = this.invitationToken;
    bodyData.accountType = this.state.accountType;
    bodyData.termsAccepted = (this.state.termsAccepted ? 'Y' : 'N');

    let result = await this.context.dbTwig.callRestAPI('newUserSignup', bodyData);
    if ('success' !== result.status)
    {
      this.setState({buttonDisabled: false});
      return this.context.dbTwig.apiErrorHandler(result, 'Error creating user account');
    } 

    this.context.postNotification('success', 'Signup successful - awaiting confirmation.');      
    this.setState({confirmationSent: true});
  }
  
  toggleAcceptTerms(field)
  {
    let termsAccepted = !this.state.termsAccepted;
    this.setState(
    {
      termsAccepted: termsAccepted
    }, 
    this.setButtonDisabled);
  }
}

Signup.contextType = AppContext;

export default Signup